import { ChangeState, FSM } from 'afsm';
import { ConnectionEvent, ConnectionState, IRoom, ITrack, MediaType, PlayOption, PlayerState, PublishedUser, RemoteStreamType, TrackEvent } from '../enums';
import loggerManager from '../utils/log/logger-manager';
import { AudioPlayer, RemoteAudioPlayer, VideoPlayer } from '../common/player';
import { NAME } from '../common/constants';
import INNER_EVENT from '../inner-emitter/inner-event';
import { PlayerEvent } from '../common/event';
import innerEmitter from '../inner-emitter/inner-emitter';
import Logger from '../utils/log/logger';
import { LocalTrack } from './local-track';
import { delay, isArray, isBoolean, isRotate90Or270, isUndefined } from '../utils/utils';
import { isRemoteTrack } from '../utils/type-check';
import generateUUID from '../utils/uuid';
import EventEmitter from 'eventemitter3';
import RtcError from '../common/rtc-error';
import { AudioManager, AudioPlayerOptions, deleteAutoPlayDialog, IRemoteTrack, isAutoPlayDialogShowing,  showAutoPlayDialog, VideoPlayerOptions } from '..';
import { VideoManager } from '../manager/video-manager';
import * as eventManager from '../manager/event-manager';
import { IS_WECHAT, IS_XWEB } from '../utils/environment';

interface TrackEventTypes {
  [TrackEvent.PLAYER_STATE_CHANGED]: [{ type: AudioOrVideo, state: PlayerState, reason: string; }];
  [TrackEvent.ERROR]: [error: RtcError | Error | unknown];
  [TrackEvent.MUTE]: [track: ITrack];
  [TrackEvent.UNMUTE]: [track: ITrack];
  [TrackEvent.INPUT_MEDIA_TRACK_CHANGED]: [track: MediaStreamTrack | null, prevTrack: MediaStreamTrack | null];
  [TrackEvent.OUTPUT_MEDIA_TRACK_CHANGED]: [track: MediaStreamTrack];
  [TrackEvent.FIRST_VIDEO_FRAME]: [{ width: number, height: number, streamType: RemoteStreamType, userId: string; }];
  [TrackEvent.VIDEO_SIZE_CHANGED]: [{ userId: string, streamType: string, newWidth: number, newHeight: number; }];
  [ConnectionEvent.CONNECTION_STATE_CHANGED]: [{ prevState: ConnectionState, state: ConnectionState; }];
}
export type TrackType<T extends AudioOrVideo> = T extends 'audio' ? MediaStreamAudioTrack : MediaStreamVideoTrack;
export type ManagerType<T extends AudioOrVideo> = T extends 'audio' ? AudioManager : VideoManager;
export type PlayerType<T extends AudioOrVideo> = T extends 'audio' ? AudioPlayer : VideoPlayer;
export type MediaTypes<T extends AudioOrVideo> = T extends 'audio' ? MediaType.AUDIO : MediaType;
export type PlayOptions<T extends AudioOrVideo> = T extends 'audio' ? AudioPlayerOptions : VideoPlayerOptions;
export type ProfileType<T extends AudioOrVideo> = T extends 'audio' ? AudioProfile : VideoProfile;
export type PlayerClassType<T extends AudioOrVideo> = new (options: PlayOptions<T>) => PlayerType<T>;


export abstract class Track<T extends AudioOrVideo, EventTypes extends EventEmitter.ValidEventTypes = string | symbol> extends FSM<EventTypes | TrackEventTypes> implements ITrack {
  // init
  id = generateUUID();
  userId = '';
  isRemote: boolean;
  mediaType!: MediaType;
  room?: IRoom;
  user?: PublishedUser;
  _log: Logger;
  _inputTrack?: PlayOptions<T>['track'];
  _outputTrack?: PlayOptions<T>['track'];
  isPlayCalled: boolean;
  container: HTMLElement | null = null;
  player: PlayerType<T>;
  // 用于在多个 view 下播放视频
  subVideoPlayerMap?: Map<HTMLElement, VideoPlayer>;
  muted = false;
  abortCtrl?: AbortController; // 终止推/拉控制
  objectFit: 'contain' | 'cover' | 'fill' = 'cover';
  mirror?: boolean | string;
  rotation?: 0 | 90 | 180 | 270;
  isScreen = false;
  manager?: ManagerType<T>;
  trackSettings?: MediaTrackSettings;
  isFirstVideoFrameEmitted = false; // 可能通过 loadedmetadata / loadeddata 触发
  onPlayerError?(event: MediaError): void;

  // as {new({track:null,muted:false,container:null,id:string,log:ILogger}) }
  // 适用于 remote track
  constructor({ userId, sdkAppId, mediaType, room, PlayerClass = (mediaType === MediaType.AUDIO ? RemoteAudioPlayer : VideoPlayer) as unknown as PlayerClassType<T> }: {
    userId?: string, sdkAppId?: number, mediaType: MediaTypes<T>; room?: IRoom, PlayerClass?: PlayerClassType<T>;
  }) {
    super();
    this.userId = userId || '';
    this.mediaType = mediaType;
    this._log = loggerManager.createLogger({
      parent: room?.getLogger(),
      id: `${this.kind[0]}t`,
      userId: (room || this.room)?.userId,
      remoteUserId: this instanceof LocalTrack ? undefined : this.userId,
      sdkAppId,
      type: this.mediaType === MediaType.AUX_VIDEO ? RemoteStreamType.Aux : RemoteStreamType.Main,
      isLocal: this instanceof LocalTrack
    });
    this.player = new PlayerClass({
      id: this.userId || this.id,
      track: null,
      muted: false,
      container: null,
      log: this._log,
      enableVolumeControlInIOS: room?.enableVolumeControlInIOS
    } as PlayOptions<T>) as unknown as PlayerType<T>;
    this.player.on(PlayerEvent.PLAYER_STATE_CHANGED, (event: any): void => {
      innerEmitter.emit(INNER_EVENT.PLAYER_STATE_CHANGED, { track: this, ...event });
      this.emit(TrackEvent.PLAYER_STATE_CHANGED, event);

      // 若所有播放器都处于播放状态，则可以关闭自动播放弹窗。
      if (event.state === PlayerState.PLAYING && this.room) {
        let isAllPlayerPlaying = true;
        for (const { remoteAudioTrack, remoteVideoTrack, remoteAuxiliaryTrack } of [...this.room.remotePublishedUserMap.values()]) {
          if ((remoteAudioTrack.isAvailable && !remoteAudioTrack.player.isPlaying) ||
              (remoteVideoTrack.isAvailable && !remoteVideoTrack.player.isPlaying) ||
              (remoteAuxiliaryTrack.isAvailable && !remoteAuxiliaryTrack.player.isPlaying))  {
            isAllPlayerPlaying = false;
            break;
          }
        }
        if (isAllPlayerPlaying && isAutoPlayDialogShowing()) deleteAutoPlayDialog();
      }
    });
    if (this.kind === NAME.VIDEO) {
      this.player.on(PlayerEvent.LOADED_DATA, (): void => {
        this.emitFirstVideoFrameEvent(PlayerEvent.LOADED_DATA);
        innerEmitter.emit(INNER_EVENT.VIDEO_LOADED_DATA, { track: this });
      });
      this.player.on(PlayerEvent.LOADED_META_DATA, (): void => {
        this.emitFirstVideoFrameEvent(PlayerEvent.LOADED_META_DATA);
      });
      this.player.on(PlayerEvent.MEDIA_TRACK_CHANGED, (track: MediaStreamVideoTrack | null): void => {
        this.subVideoPlayerMap?.forEach(subPlayer => subPlayer.setTrack(track));
      });
      this.player.on(PlayerEvent.RESIZE, (event: any): void => {
        this.emitFirstVideoFrameEvent(PlayerEvent.RESIZE);
        this.emit(TrackEvent.VIDEO_SIZE_CHANGED, { userId: this.userId, streamType: this.mediaType === MediaType.AUX_VIDEO ? RemoteStreamType.Aux : RemoteStreamType.Main, ...event });
      });
    }

    this.onTrackMuted = this.onTrackMuted.bind(this);
    this.onTrackUnmuted = this.onTrackUnmuted.bind(this);
    this.onTrackEnded = this.onTrackEnded.bind(this);
    if (this.onPlayerError) this.player.on(PlayerEvent.ERROR, this.onPlayerError.bind(this));
    this.player.on(PlayerEvent.AUTOPLAY_FAILED, this.handleAutoPlayFailed, this);
  }

  // trackEventDelegate = new EventDelegate<'mute' | 'unmute' | 'ended', Event>();
  // elementEventDelegate = new EventDelegate<'playing' | 'pause', Event>();

  get log(): Logger {
    return this._log || loggerManager;
  }

  get kind() {
    return (this.mediaType === MediaType.AUDIO ? NAME.AUDIO : NAME.VIDEO) as T;
  }

  get isAudio() {
    return this.kind === NAME.AUDIO;
  }

  get strMediaType() {
    if (this.mediaType === MediaType.BIG_VIDEO) return NAME.VIDEO;
    if (this.mediaType === MediaType.AUX_VIDEO) return NAME.SCREEN;
    return NAME.AUDIO;
  }
  get streamType() {
    return (this.mediaType & MediaType.AUX_VIDEO) === 0 ? RemoteStreamType.Main : RemoteStreamType.Aux;
  }

  get isMediaTrackActive() {
    if (!this.mediaTrack) return false;
    return !this.mediaTrack.muted && this.mediaTrack.readyState === 'live' && this.mediaTrack.enabled;
  }

  async play(container: HTMLElement | HTMLElement[] | null, options?: PlayOption) {
    const mainContainer = isArray(container) ? container[0] : container;
    // stream 已经播放，更新播放参数
    if (this.isPlayCalled) {
      this.log.info(`play update options: ${JSON.stringify(options)}`);
      if (options && !isUndefined(options.muted)) {
        this.setPlayerMute(options.muted);
      }
      if (options && !isUndefined(options.objectFit)) {
        this.objectFit = options.objectFit;
      }
      if (this.player instanceof VideoPlayer) {
        this.player.setObjectFit!(this.objectFit);
        if (this.container !== mainContainer && mainContainer) {
          if (isArray(container) && container.length >= 1 && this.container && container.includes(this.container) && this.container.contains(this.player.elementToRender)) {
            container.splice(container.indexOf(this.container), 1);
            container.unshift(this.container);
          } else {
            this.container = mainContainer;
            this.player.setContainer(mainContainer);
          }
        }
        if (isArray(container) && container.length >= 1) {
          await this.playSubContainer(container.slice(1), options);
        }
      }
      return;
    }

    if (options && !isUndefined(options.muted)) {
      this.setPlayerMute(options.muted);
    } else if (!this.isRemote || this.kind === NAME.VIDEO) {
      // 本地 audio video 及远端 video playerMuted 默认为 true'
      // ensure audio is muted for local stream to avoid playing back the sound from microphone
      this.setPlayerMute(true);
    }
    if (options && !isUndefined(options.objectFit)) {
      this.objectFit = options.objectFit;
    }
    if (this.player instanceof VideoPlayer) {
      // 根据 isLiveStream 设置直播模式（在 setContainer 之前调用，确保 DOM 结构正确）
      if (!isUndefined(options?.isLiveStream)) {
        this.player.setLiveMode(options.isLiveStream);
      }
      this.player.setObjectFit!(this.objectFit);
      if (options && !isUndefined(options.poster)) {
        this.player.setPoster(options.poster);
      }
    }
    // this.elementEventDelegate.changeTarget(this.player.element);

    this.isPlayCalled = true;
    if (mainContainer) {
      this.container = mainContainer;
      if (this.player instanceof VideoPlayer) this.player.setContainer(mainContainer);
    }
    innerEmitter.emit(INNER_EVENT.PLAY_TRACK_START, { track: this });
    if (!this._outputTrack) {
      this.log.info('play has not mediaTrack, abort');
      return;
    }
    this._log.info(`play with options: ${JSON.stringify(options)}`);
    try {
      this.player.setTrack(this.playerMediaTrack);
      await this.player.play(options);
      if (isArray(container) && container.length > 1) {
        await this.playSubContainer(container.slice(1), options);
      }
    } catch (error) {
      this.handleAutoPlayFailed(error);
      // 向下兼容 remoteStream.play(id).catch(error => {}) 的应用场景
      throw error;
    }
  }

  setMirror(mirror?: string | boolean, subPlayer?: VideoPlayer) {
    if (this.isScreen) return;
    if (this.kind !== NAME.VIDEO) return;
    if (isUndefined(mirror)) return;
    if (mirror === this.mirror) return;

    this.mirror = mirror;

    let player = this.player as unknown as VideoPlayer;
    if (subPlayer) {
      player = subPlayer;
    }

    const manager = this.manager as VideoManager;

    // 兼容旧逻辑和设置远端镜像，mirror 为布尔时，设置播放端镜像
    if (isBoolean(this.mirror)) {
      player.setViewMirror(this.mirror);
      if (!this.isRemote && manager) manager.mirror = false;
      return;
    }

    switch (this.mirror) {
      case 'view': {
        if (manager) manager.mirror = false;
        player.setViewMirror(true);
        break;
      }
      case 'publish': {
        if (manager) manager.mirror = true;
        player.setViewMirror(true); // 编码镜像会导致预览也翻转，此处负负得正
        break;
      }
      case 'both': {
        if (manager) manager.mirror = true;
        player.setViewMirror(false);
      }
    }
  }

  // 支持多 container 播放视频
  async playSubContainer(newSubContainerList: HTMLElement[], options?: PlayOption) {
    if (!this._outputTrack || this.kind === NAME.AUDIO) return;
    if (!this.subVideoPlayerMap) {
      this.subVideoPlayerMap = new Map();
    }
    // 停止不需要继续播放的 subPlayer
    this.subVideoPlayerMap.forEach((subPlayer, container) => {
      if (!newSubContainerList.find(subContainer => container === subContainer)) {
        subPlayer.stop();
        this.subVideoPlayerMap?.delete(container);
      }
    });

    // 遍历新的 subContainer，若已经在播放，则更新播放属性；若没有在播放则创建新 subPlayer 播放。
    for (const [index, container] of newSubContainerList.entries()) {
      const subPlayer = this.subVideoPlayerMap.get(container);
      // 存在 subPlayer 则更新播放属性
      if (subPlayer) {
        if (options) {
          if (!isUndefined(options.objectFit)) {
            subPlayer.setObjectFit(options.objectFit);
          }
        }
      } else {
        // 不存在 subPlayer 则创建。
        this.subVideoPlayerMap.set(container, new VideoPlayer({
          id: this.userId || this.id,
          track: this.playerMediaTrack as MediaStreamVideoTrack,
          container,
          muted: this.player.muted,
          objectFit: this.objectFit,
          log: this.log.createChild({ id: `vp-sub${index + 1}` })
        }));
      }
    }
    const subPlayerList = [...this.subVideoPlayerMap.values()];
    for (const subPlayer of subPlayerList) {
      subPlayer.setViewMirror((this.player as unknown as VideoPlayer).mirror);
      await subPlayer.play();
    }
  }

  setAudioOutput(deviceId: string) {
    // @ts-ignore
    return this.player.setSinkId(deviceId);
  }

  /**
   * 设置播放音量大小<br>
   *
   * @param {number} volume 音量大小，取值在 0.0 (静音) 到 1.0 (最大音量) 之间。
   */
  setAudioVolume(volume: number) {
    (this.player as unknown as AudioPlayer).setVolume(volume);
  }


  /**
   * 获取当前音量大小<br>
   *
   * @returns {number} audioLevel 音量大小
   * - 返回值在(0.0, 1.0)之间，通常认为值大于0.1为用户正在说话。
   * @example
   * setInterval(() => {
   *   const level = stream.getAudioLevel();
   *   if (level >= 0.1) {
   *     console.log(`user ${stream.getUserId()} is speaking`);
   *   }
   * }, 200);
   */
  getAudioLevel(): number {
    // @ts-ignore
    return this.player.getAudioLevel() || 0.0;
  }

  /**
   * 用于获取 0.00000 未丢失精度的音量数据
   * @private
   */
  getInternalAudioLevel() {
    // @ts-ignore
    return this.player?.getInternalAudioLevel() || 0.0;
  }

  stop(isCallByClose = false) {
    if (!this.isPlayCalled) return;
    this.isPlayCalled = false;
    this.isFirstVideoFrameEmitted = false;  // 确保重新触发首帧事件
    if (this.player) {
      this.log.info(`stop ${this.kind} player`);
      this.player.stop(isRemoteTrack(this) && !isCallByClose ? this.jitterBufferDelay : 0);
    }
    if (this.subVideoPlayerMap && this.subVideoPlayerMap.size > 0) {
      this.subVideoPlayerMap.forEach(subPlayer => {
        subPlayer.stop();
      });
    }
    this.container = null;
  }

  async resume() {
    if (!this.isPlayCalled) return;
    // audio should come first before video otherwise there will be NotAllowedError exception
    // in WebChat browser on iOS.
    await this.player?.resume();
  }

  close() {
    this._toInitState();
    this.log.info('close');
    // sanity check
    if (this.isPlayCalled) {
      this.stop(true);
    }
  }

  @ChangeState([], FSM.INIT, { sync: true })
  _toInitState() {}
  setMute(mute: boolean) {
    this.muted = mute;
    if (this._inputTrack) this._inputTrack.enabled = !mute;
    if (this._outputTrack) this._outputTrack.enabled = !mute;
    this.emit(mute ? TrackEvent.MUTE : TrackEvent.UNMUTE, this);
    innerEmitter.emit(mute ? INNER_EVENT.TRACK_MUTED : INNER_EVENT.TRACK_UNMUTED, { track: this });
  }

  setPlayerMute(mute: boolean) {
    this.player.setMuted(mute);
  }

  get mediaTrack() {
    return this._inputTrack || null;
  }
  get outMediaTrack() {
    return this._outputTrack || null;
  }
  get playerMediaTrack() {
    return this.outMediaTrack;
  }

  installTrackEvent(track: MediaStreamTrack) {
    eventManager.create(track, track)
      .add(NAME.MUTE, this.onTrackMuted)
      .add(NAME.UNMUTE, this.onTrackUnmuted)
      .add(NAME.ENDED, this.onTrackEnded);
    if (track.muted) {
      this.onTrackMuted();
    }
    if (track.readyState === NAME.ENDED) {
      this.onTrackEnded();
    }
  }

  uninstallTrackEvent(track: MediaStreamTrack) {
    eventManager.remove(track);
  }
  setInputMediaStreamTrack(input: TrackType<T>) {
    const preTrack = this._inputTrack;
    if (input === preTrack) return;
    this._inputTrack = input;
    this.trackSettings = input.getSettings?.();
    input.enabled = !this.muted;
    if (preTrack) {
      this.uninstallTrackEvent(preTrack);
    }
    this.installTrackEvent(input);
    this.emit(TrackEvent.INPUT_MEDIA_TRACK_CHANGED, input || null, preTrack || null);
    if (this.manager) return this.manager.changeInput(this);
    return this.setOutputMediaStreamTrack(input);
  }
  setOutputMediaStreamTrack(output: TrackType<T>): void | Promise<void> {
    const preTrack = this._outputTrack;
    if (output === preTrack) return;
    if (this.isRemote) {
      this.log.debug('setOutputMediaStreamTrack', output.label);
    } else {
      this.log.info('setOutputMediaStreamTrack', output.getSettings?.().deviceId, output.label);
    }
    this._outputTrack = output;

    if (this._inputTrack) {
      this._outputTrack.contentHint = this._inputTrack.contentHint;
      this._outputTrack.enabled = this._inputTrack.enabled;
    }
    this.updatePlayingState(!!output);
    this.emit(TrackEvent.OUTPUT_MEDIA_TRACK_CHANGED, output as MediaStreamTrack);
  }

  setMediaType(mediaType: MediaType) {
    this.mediaType = mediaType;
  }
  updatePlayingState(isNeedPlay: boolean) {
    if (this.isPlayCalled) {
      if (isNeedPlay) {
        this.player.setTrack(this.playerMediaTrack);
        if (this.player.isStopped) {
          this.player.play().catch((error: any) => this.handleAutoPlayFailed(error));
          this.log.info(`playing state updated, play ${this.kind}`);
          return;
        }
      } else if (!this.player.isStopped) {
        if (isRemoteTrack(this) && this.isAudio && this.user?.muteState.hasAudio && this.user?.muteState.audioMuted) return; // 远端 mute 音频不销毁 audio 标签，保持静音数据持续消费。
        this.player.stop(isRemoteTrack(this) ? this.jitterBufferDelay : 0);
        this.log.info(`playing state updated, stop ${this.kind}`);
        return;
      }
    }
    this.log.debug(`updatePlayingState abort ${this.isPlayCalled} ${isNeedPlay} ${this.player.isStopped}`);
  }

  async handleAutoPlayFailed(error: any) {
    this.log.warn('handleAutoPlayFailed', error);
    // 未开启自动播放失败弹窗，则监听全局点击事件，当用户点击时自动恢复，无需业务侧调用接口。
    const resumePlayback = () => {
      this.resume().then(() => {
        document.removeEventListener('click', resumePlayback, true);
      });
    };
    if (this.room && this.room.enableAutoPlayDialog) {
      // 微信里自动播放失败后，可能在短时间内就恢复，此时就无需弹窗。CallKit 需要尽可能减少弹窗，此逻辑属于无奈之举。
      if (IS_XWEB || IS_WECHAT) {
        await delay(100);
        if (this.player?.isPlaying) return;
      }
      showAutoPlayDialog();
    } else {
      document.addEventListener('click', resumePlayback, true);
    }

    // 采集麦克风成功后，可以恢复自动播放失败的播放
    innerEmitter.once(INNER_EVENT.LOCAL_TRACK_CAPTURE_SUCCESS, ({ track }) => {
      if (track.kind === 'audio' && isAutoPlayDialogShowing() && !this.player.isPlaying && this.isRemote && (this as unknown as IRemoteTrack).isAvailable) {
        resumePlayback();
      }
    });
    // SDK 存在自动调用 playVideo 及 playAudio 的情况
    // 在域名未被浏览器加入自动播放白名单时，自动播放会失败并产生 0x4043 错误，此时需要将错误抛出给接入侧引导用户与页面产生交互
    this.emit(TrackEvent.ERROR, error);
  }

  getVideoFrame() {
    if (this.player instanceof VideoPlayer) {
      return this.player.getVideoFrame();
    }
    return '';
  }

  emitFirstVideoFrameEvent(playerEvent: string) {
    if (this.isFirstVideoFrameEmitted) return;
    // firefox 下 settings 可能是空的, 此时用 element 的视频宽高兜底
    const settings = this.mediaTrack?.getSettings();
    let width = settings?.width || (this.player.element as HTMLVideoElement)?.videoWidth || 0;
    let height = settings?.height || (this.player.element as HTMLVideoElement)?.videoHeight || 0;
    // iOS 18.3.1 在 loadedmetadata 事件获取的 width 和 height 为 0，此时用 loadeddata 事件兜底
    // 事件触发顺序: resize -> loadedmetadata -> loadeddata
    if (playerEvent === PlayerEvent.RESIZE && !width && !height) return;
    if (playerEvent === PlayerEvent.LOADED_META_DATA && !width && !height) return;
    if (playerEvent === PlayerEvent.LOADED_DATA && !width && !height) this._log.warn('the dimension of video is 0x0 in first-video-frame event');
    this.isFirstVideoFrameEmitted = true;
    if (isRotate90Or270(this.rotation)) {
      [width, height] = [height, width];
    }
    this.emit(TrackEvent.FIRST_VIDEO_FRAME, { width, height, streamType: this.streamType, userId: this.isRemote ? this.userId : '' });
  }

  onTrackMuted() {
    this._log.warn(`${this.kind} track is unable to provide media output`);
  }
  onTrackUnmuted() {
    this._log.info(`${this.kind} track is able to provide media output`);
  }
  onTrackEnded() {
    this._log.warn(`${this.kind} track ended`);
  }
}
